import React, { useState, useEffect } from "react";
import { GAMES, GAME_ORDER } from "../../data/games";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { ShieldCheck, Lock, Plus, Upload, Trash2, Database, Link2, MousePointerClick, Download } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const AdminTab = ({ game, onGame, draws, affiliate = {}, onChanged, onAffiliateChanged }) => {
  const cfg = GAMES[game];
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState("");
  const [form, setForm] = useState({ date: "", numbers: "", bonus: "" });
  const [affUrl, setAffUrl] = useState("");
  const [clicks, setClicks] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAffUrl(affiliate[game] || "");
  }, [game, affiliate]);

  const headers = { "X-Admin-Code": code };

  const loadClicks = async (c) => {
    try {
      const { data } = await api.get("/admin/affiliate-clicks", { headers: { "X-Admin-Code": c } });
      setClicks(data);
    } catch {
      setClicks(null);
    }
  };

  const verify = async () => {
    try {
      await api.post("/admin/verify", {}, { headers: { "X-Admin-Code": pin } });
      setCode(pin);
      setAuthed(true);
      loadClicks(pin);
      toast.success("Accès administrateur autorisé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Code invalide");
    }
  };

  const addDraw = async () => {
    const numbers = form.numbers.split(/[\s,;]+/).map(Number).filter((n) => n > 0);
    const bonus = form.bonus.split(/[\s,;]+/).map(Number).filter((n) => n > 0);
    if (numbers.length !== cfg.pick && !cfg.kenoMode) {
      toast.error(`${cfg.pick} numéros attendus`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/draws/${game}`, { date: form.date || new Date().toISOString().slice(0, 10), numbers, bonus }, { headers });
      toast.success("Tirage ajouté");
      setForm({ date: "", numbers: "", bonus: "" });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/draws/${game}/import`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} tirage(s) importé(s)`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const reset = async () => {
    if (!window.confirm(`Supprimer TOUS les tirages de ${cfg.name} ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/draws/${game}`, { headers });
      toast.success("Historique réinitialisé");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveAffiliate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/affiliate-links", {
        links: { ...affiliate, [game]: affUrl },
      }, { headers });
      toast.success("Lien d'affiliation enregistré");
      onAffiliateChanged && onAffiliateChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 p-8 shadow-sm text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#0B132B] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-white" />
        </div>
        <h3 className="font-extrabold text-xl text-slate-900">Espace Administrateur</h3>
        <p className="text-slate-500 text-sm mt-1 mb-5">Saisissez le code secret pour gérer l'historique des tirages.</p>
        <Input type="password" placeholder="Code secret" value={pin} onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && verify()} className="rounded-xl h-11 text-center" data-testid="admin-pin-input" />
        <Button onClick={verify} className="w-full mt-3 rounded-full h-11 font-bold text-white bg-[#0B132B] hover:bg-[#003B80]" data-testid="admin-submit-pin">
          <ShieldCheck className="w-4 h-4 mr-1" /> Déverrouiller
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-slate-600 mr-1">Jeu à administrer :</span>
        {GAME_ORDER.map((g) => (
          <button key={g} onClick={() => onGame(g)} data-testid={`admin-game-${g}`}
            className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all ${g === game ? "text-white shadow" : "bg-slate-100 text-slate-600"}`}
            style={g === game ? { background: GAMES[g].primary } : {}}>
            {GAMES[g].name}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-tirage-form">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4"><Plus className="w-5 h-5" style={{ color: cfg.primary }} /> Ajouter le dernier tirage</h3>
          <label className="text-xs font-bold uppercase text-slate-400">Date</label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mb-3 mt-1" data-testid="admin-date-input" />
          <label className="text-xs font-bold uppercase text-slate-400">Numéros principaux (séparés par des espaces)</label>
          <Input placeholder={`ex: ${Array.from({ length: cfg.pick }, (_, i) => i + 3).join(" ")}`} value={form.numbers}
            onChange={(e) => setForm({ ...form, numbers: e.target.value })} className="rounded-xl mb-3 mt-1" data-testid="admin-numbers-input" />
          {cfg.bonusPick > 0 && (
            <>
              <label className="text-xs font-bold uppercase text-slate-400">{cfg.bonusName}</label>
              <Input placeholder="ex: 4" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} className="rounded-xl mb-3 mt-1" data-testid="admin-bonus-input" />
            </>
          )}
          <Button onClick={addDraw} disabled={busy} className="w-full rounded-full font-bold text-white" style={{ background: cfg.primary }} data-testid="admin-add-draw-btn">
            Enregistrer le tirage
          </Button>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4"><Upload className="w-5 h-5" style={{ color: cfg.primary }} /> Importer un historique</h3>
            <p className="text-sm text-slate-500 mb-3">Fichiers .csv ou .xlsx (colonnes : date, n1..nX, bonus).</p>
            <div className="flex flex-wrap items-center gap-2">
              <label className="block">
                <input type="file" accept=".csv,.xlsx" onChange={upload} className="hidden" data-testid="admin-import-input" />
                <span className="inline-flex items-center gap-2 rounded-full font-bold px-5 py-2.5 bg-slate-100 hover:bg-slate-200 cursor-pointer text-slate-700 transition-all">
                  <Upload className="w-4 h-4" /> Choisir un fichier
                </span>
              </label>
              <a href={`/templates/modele_${game}.csv`} download
                className="inline-flex items-center gap-2 rounded-full font-bold px-5 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 transition-all"
                style={{ color: cfg.primary }} data-testid="admin-download-template">
                <Download className="w-4 h-4" /> Télécharger le modèle
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><Database className="w-5 h-5" style={{ color: cfg.primary }} /> Historique actuel</h3>
            <p className="text-sm text-slate-500 mb-4"><span className="font-black text-2xl text-slate-800">{draws.length}</span> tirages enregistrés pour {cfg.name}.</p>
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="admin-reset-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Réinitialiser l'historique
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-1"><Link2 className="w-5 h-5" style={{ color: cfg.primary }} /> Lien d'affiliation FDJ — {cfg.name}</h3>
        <p className="text-sm text-slate-500 mb-4">Collez votre lien de tracking Kwanko pour ce jeu. Laissez vide pour utiliser la page officielle FDJ par défaut.</p>

        <label className="text-xs font-bold uppercase text-slate-400">Lien de tracking (clic)</label>
        <Input value={affUrl} onChange={(e) => setAffUrl(e.target.value)} placeholder="https://track.kwanko.com/..." className="rounded-xl mb-4 mt-1" data-testid="admin-affiliate-input" />

        <Button onClick={saveAffiliate} disabled={busy} className="rounded-full font-bold text-white" style={{ background: cfg.primary }} data-testid="admin-affiliate-save-btn">
          Enregistrer le lien
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-clicks-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><MousePointerClick className="w-5 h-5" style={{ color: cfg.primary }} /> Suivi des clics FDJ</h3>
          <Button onClick={() => loadClicks(code)} variant="outline" size="sm" className="rounded-full font-bold" data-testid="admin-clicks-refresh">Rafraîchir</Button>
        </div>
        {!clicks ? (
          <p className="text-sm text-slate-400">Aucune donnée pour le moment.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {GAME_ORDER.map((g) => (
              <div key={g} className="rounded-xl bg-slate-50 p-3 text-center" data-testid={`admin-clicks-${g}`}>
                <div className="text-[10px] font-bold uppercase text-slate-400 truncate">{GAMES[g].name}</div>
                <div className="text-2xl font-black" style={{ color: GAMES[g].primary }}>{clicks.counts?.[g] ?? 0}</div>
              </div>
            ))}
            <div className="rounded-xl p-3 text-center text-white" style={{ background: cfg.primary }} data-testid="admin-clicks-total">
              <div className="text-[10px] font-bold uppercase text-white/70">Total</div>
              <div className="text-2xl font-black">{clicks.total ?? 0}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
